import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { menuModifierService } from '../../../../services/menuModifierService';
import { menuItemService } from '../../../../services/menuItemService';
import { MenuItemModifierDTO, MenuItemListDTO } from '../../../../types';
import { ArrowLeft, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import '../RestaurantPage.css';

const MenuModifierListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState<MenuItemListDTO[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | ''>('');
  const [modifiers, setModifiers] = useState<MenuItemModifierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>((location.state as any)?.message ?? null);

  useEffect(() => {
    menuItemService.getAll(1, 200).then(res => {
      const d = (res.data as any)?.data?.data ?? (res.data as any)?.data ?? [];
      setMenuItems(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedItemId !== '') { loadModifiers(); }
    else { setModifiers([]); }
  }, [selectedItemId]); // eslint-disable-line

  const loadModifiers = async () => {
    if (selectedItemId === '') return;
    setLoading(true);
    try {
      const res = await menuModifierService.getByMenuItem(selectedItemId as number);
      const d = (res.data as any)?.data ?? [];
      setModifiers(Array.isArray(d) ? d : []);
    } catch { setError('Failed to load modifiers.'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this modifier?')) return;
    setDeleting(id);
    try {
      await menuModifierService.delete(id);
      setModifiers(prev => prev.filter(m => m.id !== id));
      setSuccessMsg('Modifier deleted.');
    } catch { setError('Failed to delete modifier.'); }
    finally { setDeleting(null); }
  };

  const MODIFIER_TYPE_LABELS: Record<string, string> = { AddOn: 'Add-on', Removal: 'Removal', LevelSelection: 'Level' };

  return (
    <Layout>
      <div className="rest-list-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/menu-items')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">Menu Modifiers</div>
          </div>
          <div className="ims-top-bar-right">
            <button className="ims-btn ims-btn-primary"
              onClick={() => navigate('/restaurant/modifiers/create', { state: { menuItemId: selectedItemId } })}
              disabled={selectedItemId === ''}>
              <Plus size={14} strokeWidth={2} /> Add Modifier
            </button>
          </div>
        </div>

        {successMsg && <div className="ims-alert ims-alert-success" style={{ marginTop: '1rem' }}>{successMsg}<button className="btn-close" onClick={() => setSuccessMsg(null)} /></div>}
        {error && <div className="ims-alert ims-alert-error" style={{ marginTop: '1rem' }}>{error}<button className="btn-close" onClick={() => setError(null)} /></div>}

        <div className="ims-card" style={{ marginTop: '1rem' }}>
          <div className="ims-card-body">
            <div className="ims-form-group" style={{ maxWidth: '400px' }}>
              <label className="ims-form-label">Select Menu Item</label>
              <select className="ims-form-control" value={selectedItemId}
                onChange={e => setSelectedItemId(e.target.value === '' ? '' : parseInt(e.target.value))}>
                <option value="">— Choose a menu item —</option>
                {menuItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {selectedItemId !== '' && (
          <div className="ims-card" style={{ marginTop: '1rem' }}>
            <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Loader2 size={20} /></div>
              ) : modifiers.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ims-text-sub)', fontSize: '13px' }}>
                  No modifiers for this item. <button className="ims-btn ims-btn-primary" style={{ marginLeft: '8px', padding: '4px 10px', fontSize: '12px' }}
                    onClick={() => navigate('/restaurant/modifiers/create', { state: { menuItemId: selectedItemId } })}>Add one</button>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>{['Name','Type','Price Impact','Description','Active',''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', background: 'var(--ims-bg-th, #f9fafb)', fontWeight: 600, fontSize: '11px', color: 'var(--ims-text-sub)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {modifiers.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--ims-border, #e5e7eb)' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 500 }}>{m.name}</td>
                        <td style={{ padding: '10px 14px' }}><span className="ims-badge">{MODIFIER_TYPE_LABELS[m.modifierType] ?? m.modifierType}</span></td>
                        <td style={{ padding: '10px 14px' }}>{m.additionalPrice > 0 ? `+$${Number(m.additionalPrice).toFixed(2)}` : m.additionalPrice < 0 ? `-$${Math.abs(m.additionalPrice).toFixed(2)}` : '—'}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--ims-text-sub)' }}>{m.description || '—'}</td>
                        <td style={{ padding: '10px 14px' }}><span className={`ims-badge ${m.isActive ? 'badge-active' : 'badge-inactive'}`}>{m.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td style={{ padding: '10px 14px', display: 'flex', gap: '6px' }}>
                          <button className="ims-btn ims-btn-secondary" style={{ padding: '4px 8px' }}
                            onClick={() => navigate(`/restaurant/modifiers/${m.id}/edit`, { state: { modifier: m } })}>
                            <Pencil size={12} />
                          </button>
                          <button className="btn-delete" style={{ padding: '4px 8px' }} disabled={deleting === m.id}
                            onClick={() => handleDelete(m.id)}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MenuModifierListPage;
